// CSRF-aware REST client for /api/v1 (control-plane spec §Web cookie
// hardening): the session lives in an HttpOnly cookie set by
// POST /auth/web-login, and every cookie-authenticated MUTATING request must
// carry `X-Cairn-CSRF: 1` — a custom header forces a CORS preflight
// cross-origin, which same-origin-only CORS denies, so CSRF is structurally
// blocked. GET/HEAD/OPTIONS never need the header.

import type { HttpClient } from "@cairn/core";

/** Header required on cookie-authed mutations (control-plane middleware). */
export const CSRF_HEADER = "X-Cairn-CSRF";

/** Methods that mutate and therefore need the CSRF header. */
export function isMutating(method: string): boolean {
  const m = method.toUpperCase();
  return m !== "GET" && m !== "HEAD" && m !== "OPTIONS";
}

/** A non-2xx API response, decoded from the `{error:{code,message}}` envelope. */
export class ApiFailure extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiFailure";
  }
}

export interface ApiResponse<T> {
  status: number;
  body: T;
}

/**
 * Thin fetch wrapper. `base` is empty in the browser (same-origin); tests and
 * the integration harness pass an absolute base plus extra headers (e.g. a
 * manually-managed Cookie, since Node's fetch has no cookie jar).
 */
export class ApiClient {
  constructor(
    private readonly base = "",
    private readonly extraHeaders: Record<string, string> = {},
    private readonly fetchImpl: typeof fetch = (...args) => fetch(...args),
  ) {}

  /** Raw request: resolves with the status + parsed JSON, never throws on 4xx/5xx. */
  async request<T>(opts: {
    method: string;
    path: string;
    body?: unknown;
    headers?: Record<string, string>;
  }): Promise<ApiResponse<T>> {
    const headers: Record<string, string> = { ...this.extraHeaders, ...opts.headers };
    if (opts.body !== undefined) headers["content-type"] = "application/json";
    if (isMutating(opts.method)) headers[CSRF_HEADER] = "1";
    const res = await this.fetchImpl(`${this.base}${opts.path}`, {
      method: opts.method,
      headers,
      credentials: "same-origin",
      ...(opts.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
    });
    const text = await res.text();
    // The SPA fallback returns 404 with a NON-JSON body for reserved-prefix
    // misses (decisions/22), and gateway/proxy error pages are HTML. Parsing
    // unconditionally would throw a raw SyntaxError that `call()` can't turn
    // into a typed ApiFailure, so callers branching on status/code mis-handle
    // it. Fall back to a synthesized error envelope carrying a text snippet (m7).
    let body: T;
    if (!text) {
      body = null as T;
    } else {
      try {
        body = JSON.parse(text) as T;
      } catch {
        body = {
          error: {
            code: "NON_JSON_RESPONSE",
            message: text.slice(0, 200),
          },
        } as T;
      }
    }
    return { status: res.status, body };
  }

  /** Request that throws a decoded [`ApiFailure`] on any non-2xx status. */
  async call<T>(method: string, path: string, body?: unknown): Promise<T> {
    const res = await this.request<T>({
      method,
      path,
      ...(body !== undefined ? { body } : {}),
    });
    if (res.status >= 200 && res.status < 300) return res.body;
    const err = res.body as unknown as { error?: { code?: string; message?: string } } | null;
    throw new ApiFailure(
      res.status,
      err?.error?.code ?? "UNKNOWN",
      err?.error?.message ?? `request failed: ${res.status}`,
    );
  }

  /** Adapter to @cairn/core's HttpClient port (HistoryClient etc.). */
  asHttpClient(): HttpClient {
    return {
      request: (opts) => this.request(opts),
      putBinary: async (url, data) => {
        const res = await this.fetchImpl(url, { method: "PUT", body: data });
        return { status: res.status };
      },
      getBinary: async (url) => {
        const res = await this.fetchImpl(url);
        return { status: res.status, body: await res.arrayBuffer() };
      },
    };
  }
}

// --- typed endpoints ---------------------------------------------------------

/** Server-wide role, in ascending privilege. The API is the real gate; the UI
 *  mirrors it to hide what the caller can't do (spec §Management features). */
export type ServerRole = "guest" | "member" | "manager" | "admin";
export type VaultRole = "owner" | "editor" | "viewer";

export interface UserInfo {
  id: string;
  display_name: string;
  server_role: ServerRole;
}

export interface VaultSummary {
  id: string;
  name: string;
  created_at: number;
  attachment_quota_bytes?: number;
}

export interface VaultInfo {
  vault: VaultSummary;
  role: VaultRole;
  member_count: number;
}

export interface SyncTokenGrant {
  sync_token: string;
  ws_url: string;
  expires_at: number;
  role: "owner" | "editor" | "viewer";
}

/** POST /auth/web-login — sets the hardened session cookie on success.
 *  A 401 with code TOTP_REQUIRED means: prompt for a code and retry. */
export function webLogin(
  api: ApiClient,
  req: { email: string; password: string; totp_code?: string },
): Promise<{ user: UserInfo }> {
  return api.call("POST", "/api/v1/auth/web-login", {
    ...req,
    device_name: "web viewer",
  });
}

export function logout(api: ApiClient): Promise<void> {
  return api.call("POST", "/api/v1/auth/logout");
}

export function me(api: ApiClient): Promise<{ user: UserInfo }> {
  return api.call("GET", "/api/v1/me");
}

export function listVaults(api: ApiClient): Promise<VaultInfo[]> {
  return api.call("GET", "/api/v1/vaults");
}

/** POST /vaults/:id/token — the viewer's sync-token mint (cookie + CSRF). */
export function mintSyncToken(api: ApiClient, vaultId: string): Promise<SyncTokenGrant> {
  return api.call("POST", `/api/v1/vaults/${vaultId}/token`, {});
}

/** Presigned attachment GET (images inline, others as downloads). */
export async function attachmentUrl(
  api: ApiClient,
  vaultId: string,
  hash: string,
): Promise<string> {
  const res = await api.call<{ download_url: string }>(
    "GET",
    `/api/v1/vaults/${vaultId}/attachments/${hash}`,
  );
  return res.download_url;
}

// --- W2 management endpoints -------------------------------------------------
// Every mutation below is cookie-authed and carries X-Cairn-CSRF automatically
// (see ApiClient.request). Paths are all /api/v1/* — the web app never targets
// /admin/v1. Role gating here is UI-only; the server is the real gate.

/** A friendly message for an error, preferring the API's decoded message. */
export function errorText(err: unknown): string {
  if (err instanceof ApiFailure) return err.message;
  return err instanceof Error ? err.message : String(err);
}

/** The error code if this is a decoded API failure, else undefined. */
export function errorCode(err: unknown): string | undefined {
  return err instanceof ApiFailure ? err.code : undefined;
}

// Vaults ---------------------------------------------------------------------

export function createVault(api: ApiClient, name: string): Promise<{ vault: VaultSummary }> {
  return api.call("POST", "/api/v1/vaults", { name });
}

export interface Member {
  user: { id: string; display_name: string };
  role: VaultRole;
  created_at: number;
}

export function listMembers(api: ApiClient, vaultId: string): Promise<Member[]> {
  return api.call("GET", `/api/v1/vaults/${vaultId}/members`);
}

export interface Membership {
  user_id: string;
  vault_id: string;
  role: VaultRole;
  created_at: number;
}

export function addMember(
  api: ApiClient,
  vaultId: string,
  userId: string,
  role: VaultRole,
): Promise<{ membership: Membership }> {
  return api.call("POST", `/api/v1/vaults/${vaultId}/members`, { user_id: userId, role });
}

export function setMemberRole(
  api: ApiClient,
  vaultId: string,
  userId: string,
  role: VaultRole,
): Promise<{ membership: Membership }> {
  return api.call("PUT", `/api/v1/vaults/${vaultId}/members/${userId}`, { role });
}

export function removeMember(api: ApiClient, vaultId: string, userId: string): Promise<void> {
  return api.call("DELETE", `/api/v1/vaults/${vaultId}/members/${userId}`);
}

// Users ----------------------------------------------------------------------

/** GET /api/v1/users — the member+ directory (picker feed): no email/disabled. */
export function listUsers(api: ApiClient): Promise<UserInfo[]> {
  return api.call("GET", "/api/v1/users");
}

export interface AdminUser {
  id: string;
  display_name: string;
  server_role: ServerRole;
  created_at: number;
  disabled: boolean;
}

/** GET /api/v1/server/users — the admin roster (includes disabled + created_at). */
export function listServerUsers(api: ApiClient): Promise<AdminUser[]> {
  return api.call("GET", "/api/v1/server/users");
}

export interface InviteLink {
  invite_url: string;
  expires_at: number;
  display_name_hint?: string | null;
}

export function createAccountInvite(
  api: ApiClient,
  req: { display_name_hint?: string; email?: string },
): Promise<InviteLink> {
  return api.call("POST", "/api/v1/account-invites", req);
}

/** A pending (unused) account invite, as listed by `GET /api/v1/account-invites`. */
export interface PendingInvite {
  id: string;
  /** Invitee email, if one was recorded when the invite was created. */
  email: string | null;
  /** Inviter user id. */
  created_by: string;
  /** Inviter display name, resolved via a users join (null for CLI sentinels). */
  created_by_name: string | null;
  created_at: number;
  expires_at: number;
  /** True when the invite has passed its expiry but is still unused (cleanup). */
  expired: boolean;
}

/** GET /api/v1/account-invites — pending account invites, newest first (manager+). */
export function listAccountInvites(api: ApiClient): Promise<PendingInvite[]> {
  return api.call("GET", "/api/v1/account-invites");
}

/** DELETE /api/v1/account-invites/:id — cancel a pending account invite (manager+). */
export function cancelAccountInvite(api: ApiClient, id: string): Promise<void> {
  return api.call("DELETE", `/api/v1/account-invites/${id}`);
}

/**
 * POST /api/v1/account-invites/:id/regenerate — rotate a pending invite's token
 * (manager+). Returns a fresh link (shown once) and its new expiry; the previous
 * link stops working. The raw token is never recoverable, so a lost link is
 * regenerated, not retrieved.
 */
export function regenerateAccountInvite(
  api: ApiClient,
  id: string,
): Promise<{ invite_url: string; expires_at: number }> {
  return api.call("POST", `/api/v1/account-invites/${id}/regenerate`);
}

export function setUserRole(
  api: ApiClient,
  userId: string,
  role: ServerRole,
): Promise<{ user: AdminUser }> {
  return api.call("POST", `/api/v1/users/${userId}/set-role`, { role });
}

export function setUserDisabled(
  api: ApiClient,
  userId: string,
  disabled: boolean,
): Promise<{ user: AdminUser }> {
  return api.call("PATCH", `/api/v1/users/${userId}`, { disabled });
}

export function passwordResetLink(api: ApiClient, userId: string): Promise<{ invite_url: string }> {
  return api.call("POST", `/api/v1/users/${userId}/password-reset`);
}

export function clearUserTotp(api: ApiClient, userId: string): Promise<void> {
  return api.call("DELETE", `/api/v1/users/${userId}/totp`);
}

/** DELETE /api/v1/users/:id — surfaces LAST_ADMIN / OWNS_VAULTS. */
export function deleteUser(api: ApiClient, userId: string): Promise<void> {
  return api.call("DELETE", `/api/v1/users/${userId}`);
}

// Server ops -----------------------------------------------------------------

export interface ServerStats {
  users: number;
  vaults: number;
  docs: number;
  doc_bytes: number;
  attachments: number;
  attachment_bytes: number;
  active_sessions: number;
}

export function serverStats(api: ApiClient): Promise<ServerStats> {
  return api.call("GET", "/api/v1/server/stats");
}

export interface ServerVault {
  vault: VaultSummary;
  deleted: boolean;
  member_count: number;
  attachment_bytes: number;
}

export function listServerVaults(api: ApiClient): Promise<ServerVault[]> {
  return api.call("GET", "/api/v1/server/vaults");
}

export function setVaultQuota(
  api: ApiClient,
  vaultId: string,
  bytes: number,
): Promise<{ vault: VaultSummary }> {
  return api.call("PATCH", `/api/v1/server/vaults/${vaultId}`, {
    attachment_quota_bytes: bytes,
  });
}

export interface GcResult {
  docs_deleted: number;
  attachments_deleted: number;
  bytes_reclaimed: number;
}

export function runGc(api: ApiClient): Promise<GcResult> {
  return api.call("POST", "/api/v1/server/gc");
}

export interface CompactionResult {
  docs_compacted: number;
  bytes_before: number;
  bytes_after: number;
}

export function runCompaction(api: ApiClient): Promise<CompactionResult> {
  return api.call("POST", "/api/v1/server/compaction");
}

// Account / self -------------------------------------------------------------

export interface Session {
  id: string;
  device_name: string;
  last_seen_at: number;
  expires_at: number;
  current: boolean;
}

export interface MeFull {
  user: UserInfo & { created_at: number; disabled: boolean };
  identities: string[];
  totp_enrolled: boolean;
  sessions: Session[];
}

export function meFull(api: ApiClient): Promise<MeFull> {
  return api.call("GET", "/api/v1/me");
}

/** PATCH /me — update your own display name (email/login stays fixed). */
export function updateDisplayName(
  api: ApiClient,
  displayName: string,
): Promise<{ user: UserInfo }> {
  return api.call("PATCH", "/api/v1/me", { display_name: displayName });
}

export function revokeSession(api: ApiClient, sessionId: string): Promise<void> {
  return api.call("DELETE", `/api/v1/me/sessions/${sessionId}`);
}

export interface TotpEnrollment {
  otpauth_url: string;
  secret: string;
}

export function totpEnroll(api: ApiClient): Promise<TotpEnrollment> {
  return api.call("POST", "/api/v1/me/totp");
}

export function totpConfirm(api: ApiClient, code: string): Promise<void> {
  return api.call("POST", "/api/v1/me/totp/confirm", { code });
}

export function totpDisable(api: ApiClient, code: string): Promise<void> {
  return api.call("DELETE", "/api/v1/me/totp", { code });
}
